/**
 * =========================================================================================
 *     Copyright © 2025 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   useCapacityMetrics.ts
 *     Description     :   React Query hooks for capacity metrics data
 *     Author          :   Peter Dobler
 *     Created         :   2026-01-19
 *     Modified        :   2026-01-19 | Peter Dobler | Initial implementation
 *     Constitution    :   v1.11.0 (Specification Testing Standards Edition)
 *     Feature         :   030-capacity-metrics (T065)
 * =========================================================================================
 */

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import * as capacityMetricsApi from '../services/capacityMetricsApi';
import type {
  DashboardResponse,
  DashboardFilters,
  HourlyMetricsQuery,
  HourlyMetricsResponse,
  DailyMetricsQuery,
  DailyMetricsResponse,
  CapacityDetailResponse,
  ActiveAlertsResponse,
} from '../services/capacityMetricsApi';

// ============================================================================
// Query Keys
// ============================================================================

export const capacityMetricsKeys = {
  all: ['capacity-metrics'] as const,
  dashboard: (filters?: DashboardFilters) =>
    [...capacityMetricsKeys.all, 'dashboard', filters] as const,
  hourlyMetrics: (query?: HourlyMetricsQuery) =>
    [...capacityMetricsKeys.all, 'hourly', query] as const,
  dailyMetrics: (query?: DailyMetricsQuery) =>
    [...capacityMetricsKeys.all, 'daily', query] as const,
  capacityDetail: (capacityId: string, historyHours?: number) =>
    [...capacityMetricsKeys.all, 'detail', capacityId, historyHours] as const,
  activeAlerts: (capacityId?: string, severity?: string[]) =>
    [...capacityMetricsKeys.all, 'alerts', 'active', { capacityId, severity }] as const,
};

// ============================================================================
// Dashboard Hooks
// ============================================================================

/**
 * Hook to fetch dashboard data with sparklines and trends (T065)
 * Performance target: <2 seconds
 */
export function useCapacityDashboard(
  filters?: DashboardFilters,
  options?: Omit<UseQueryOptions<DashboardResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery<DashboardResponse>({
    queryKey: capacityMetricsKeys.dashboard(filters),
    queryFn: () => capacityMetricsApi.getDashboard(filters),
    staleTime: 30_000, // 30 seconds
    refetchInterval: 60_000, // Refetch every minute for live updates
    ...options,
  });
}

// ============================================================================
// Metrics Hooks
// ============================================================================

/**
 * Hook to fetch hourly metrics with time range filtering
 */
export function useHourlyMetrics(
  query?: HourlyMetricsQuery,
  options?: Omit<UseQueryOptions<HourlyMetricsResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery<HourlyMetricsResponse>({
    queryKey: capacityMetricsKeys.hourlyMetrics(query),
    queryFn: () => capacityMetricsApi.getHourlyMetrics(query),
    staleTime: 60_000, // 1 minute
    ...options,
  });
}

/**
 * Hook to fetch daily metrics
 */
export function useDailyMetrics(
  query?: DailyMetricsQuery,
  options?: Omit<UseQueryOptions<DailyMetricsResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery<DailyMetricsResponse>({
    queryKey: capacityMetricsKeys.dailyMetrics(query),
    queryFn: () => capacityMetricsApi.getDailyMetrics(query),
    staleTime: 300_000, // 5 minutes - daily data doesn't change often
    ...options,
  });
}

// ============================================================================
// Capacity Detail Hooks
// ============================================================================

/**
 * Hook to fetch detailed capacity info with history and attribution
 */
export function useCapacityDetail(
  capacityId: string,
  historyHours: number = 24,
  options?: Omit<UseQueryOptions<CapacityDetailResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery<CapacityDetailResponse>({
    queryKey: capacityMetricsKeys.capacityDetail(capacityId, historyHours),
    queryFn: () => capacityMetricsApi.getCapacityDetail(capacityId, historyHours),
    staleTime: 30_000,
    enabled: !!capacityId,
    ...options,
  });
}

// ============================================================================
// Alerts Hooks
// ============================================================================

/**
 * Hook to fetch active alerts
 */
export function useActiveAlerts(
  capacityId?: string,
  severity?: string[],
  options?: Omit<UseQueryOptions<ActiveAlertsResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery<ActiveAlertsResponse>({
    queryKey: capacityMetricsKeys.activeAlerts(capacityId, severity),
    queryFn: () => capacityMetricsApi.getActiveAlerts(capacityId, severity),
    staleTime: 30_000,
    refetchInterval: 60_000,
    ...options,
  });
}

/**
 * Hook to acknowledge an alert
 */
export function useAcknowledgeAlert() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      capacityId,
      acknowledgedBy,
    }: {
      capacityId: string;
      acknowledgedBy?: string;
    }) => capacityMetricsApi.acknowledgeAlert(capacityId, acknowledgedBy),
    onSuccess: () => {
      // Invalidate alerts queries to refetch
      queryClient.invalidateQueries({ queryKey: capacityMetricsKeys.all });
    },
  });
}

// ============================================================================
// Utility Hooks
// ============================================================================

/**
 * Hook to get a single capacity from the dashboard data
 */
export function useCapacityFromDashboard(capacityId: string) {
  const { data: dashboard, isLoading, error } = useCapacityDashboard();

  const capacity = dashboard?.capacities?.find((c) => c.capacity_id === capacityId);

  return {
    data: capacity,
    isLoading,
    error,
    isNotFound: !isLoading && !capacity,
  };
}

/**
 * Hook to prefetch capacity detail on hover
 */
export function usePrefetchCapacityDetail() {
  const queryClient = useQueryClient();

  return (capacityId: string) => {
    queryClient.prefetchQuery({
      queryKey: capacityMetricsKeys.capacityDetail(capacityId, 24),
      queryFn: () => capacityMetricsApi.getCapacityDetail(capacityId, 24),
      staleTime: 30_000,
    });
  };
}
